import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Menu,
  X,
  Wheat,
  Truck,
  ShieldCheck,
  Award,
  Phone,
  Mail,
  MapPin,
  ChevronDown,
  Star,
  Leaf,
  Factory,
  Package,
  Clock,
  Users,
  MessageCircle,
} from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#home", label: "Home" },
    { href: "#about", label: "About" },
    { href: "#mills", label: "Our Mills" },
    { href: "#products", label: "Products" },
    { href: "#testimonials", label: "Testimonials" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-4 left-4 right-4 md:left-8 md:right-8 lg:left-12 lg:right-12 z-50 rounded-full transition-all duration-300 ${
        isScrolled ? "nav-glass shadow-lg" : "bg-white/60 backdrop-blur-sm"
      }`}
    >
      <div className="flex items-center justify-between px-6 py-4">
        <a href="#home" className="flex items-center gap-2" data-testid="logo-link">
          <img 
            src="https://customer-assets.emergentagent.com/job_grain-grinder-1/artifacts/7d57xsgc_AMBATI%20LOGO-2.png" 
            alt="Ambati Ventures Logo" 
            className="h-10 w-10 object-contain"
          />
          <span className="font-heading text-xl md:text-2xl font-bold text-forest">
            AMBATI VENTURES
          </span>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="link-underline text-forest/80 hover:text-forest font-medium transition-colors duration-200"
              data-testid={`nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>
          ))}
          <Button
            className="bg-gold hover:bg-gold-dark text-white rounded-full px-6 font-medium transition-colors duration-200"
            data-testid="nav-cta-button"
          >
            Get Quote
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-forest" />
          ) : (
            <Menu className="h-6 w-6 text-forest" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-lg py-4 px-6"
          data-testid="mobile-menu"
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="block py-3 text-forest/80 hover:text-forest font-medium border-b border-border/50 last:border-0"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <Button
            className="w-full mt-4 bg-gold hover:bg-gold-dark text-white rounded-full font-medium transition-colors duration-200"
            data-testid="mobile-cta-button"
          >
            Get Quote
          </Button>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="home"
      className="hero-bg min-h-screen flex items-center justify-center relative grain-overlay"
      data-testid="hero-section"
    >
      <div className="relative z-10 text-center px-6 md:px-12 max-w-5xl mx-auto">
        <p className="font-accent text-2xl md:text-3xl text-gold mb-4 animate-fade-up">
          Since 1982
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 animate-fade-up" style={{ animationDelay: "0.1s" }}>
          AMBATI VENTURES
          <br />
          <span className="text-gold">Premium Rice Processing</span>
        </h1>
        <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-10 font-body animate-fade-up" style={{ animationDelay: "0.2s" }}>
          Three state-of-the-art rice mills in Karnataka delivering finest quality 
          Sona Masoori, Kolam & RNR rice varieties to your doorstep.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up" style={{ animationDelay: "0.3s" }}>
          <Button
            className="btn-gold text-forest-dark rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl"
            data-testid="hero-cta-primary"
          >
            Explore Products
          </Button>
          <Button
            variant="outline"
            className="border-2 border-white text-white hover:bg-white hover:text-forest rounded-full px-8 py-6 text-lg font-semibold transition-colors duration-200"
            data-testid="hero-cta-secondary"
          >
            Contact Us
          </Button>
        </div>
        <a
          href="#about"
          className="inline-block mt-16 text-white/70 hover:text-white animate-bounce"
          data-testid="scroll-indicator"
        >
          <ChevronDown className="h-8 w-8" />
        </a>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section
      id="about"
      className="py-20 md:py-32 bg-bone relative overflow-hidden"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-card img-zoom">
              <img
                src="https://customer-assets.emergentagent.com/job_grain-grinder-1/artifacts/buhaycl5_Screenshot%202026-01-29%20at%209.35.27%E2%80%AFPM.png"
                alt="AMBATI VENTURES Rice Mill Facility"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 md:-bottom-8 md:-right-8 bg-white rounded-2xl p-6 shadow-card-hover">
              <p className="font-heading text-4xl md:text-5xl font-bold text-gold">43+</p>
              <p className="text-forest/80 font-medium">Years of Excellence</p>
            </div>
          </div>

          {/* Content Side */}
          <div>
            <p className="text-sm tracking-widest uppercase font-semibold text-gold mb-4">
              Our Story
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-forest mb-6">
              A Legacy of Quality
              <br />
              <span className="text-gold">Since 1982</span>
            </h2>
            <p className="text-lg text-forest/70 leading-relaxed mb-6">
              AMBATI VENTURES has been a trusted name in rice processing for over four decades. 
              With three specialized rice mills in Raichur District, Karnataka, we process 
              Steam Rice, Raw Rice, and Parboiled Rice with unmatched quality standards.
            </p>
            <p className="text-lg text-forest/70 leading-relaxed mb-8">
              In 2019, we expanded our reach by establishing a trading firm in Bangalore, 
              serving e-commerce platforms and supermarkets across the city with premium 
              quality rice and rice byproducts.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center">
                  <Factory className="h-6 w-6 text-forest" />
                </div>
                <div>
                  <p className="font-semibold text-forest">3 Rice Mills</p>
                  <p className="text-sm text-forest/60">Raichur, Karnataka</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center">
                  <Truck className="h-6 w-6 text-forest" />
                </div>
                <div>
                  <p className="font-semibold text-forest">Bangalore Hub</p>
                  <p className="text-sm text-forest/60">Since 2019</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Mills Section
const MillsSection = () => {
  const mills = [
    {
      name: "Shree Kedharalakshmi Agro Foods",
      type: "Steam Rice Processing Plant",
      description: "State-of-the-art steam rice processing facility producing premium quality steam rice with advanced technology.",
      logo: "https://customer-assets.emergentagent.com/job_grain-grinder-1/artifacts/sjda6ti3_Screenshot%202026-01-29%20at%209.33.27%E2%80%AFPM.png",
    },
    {
      name: "Shree Maatha Agro Tech",
      type: "Raw Rice Processing Plant",
      description: "Specialized in processing finest quality raw rice varieties including Sona Masoori, Kolam, and RNR.",
      logo: "https://customer-assets.emergentagent.com/job_grain-grinder-1/artifacts/mtpkovr9_Screenshot%202026-01-29%20at%209.33.37%E2%80%AFPM.png",
    },
    {
      name: "Parjanya Parboiled Rice Industries",
      type: "Parboiled Rice Processing Plant",
      description: "Dedicated parboiled rice processing unit delivering nutritious and flavorful boiled rice variants.",
      logo: "https://customer-assets.emergentagent.com/job_grain-grinder-1/artifacts/b8lc9cxy_Screenshot%202026-01-29%20at%209.33.44%E2%80%AFPM.png",
    },
    {
      name: "Gurubheema Traders",
      type: "Trading Firm - Bangalore",
      description: "Our Bangalore trading hub established in 2019, catering to e-commerce platforms and supermarkets across the city.",
      logo: "https://customer-assets.emergentagent.com/job_grain-grinder-1/artifacts/f26a5a48_Screenshot%202026-01-29%20at%209.34.02%E2%80%AFPM.png",
    },
  ];

  return (
    <section
      id="mills"
      className="py-20 md:py-32 bg-white relative"
      data-testid="mills-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-sm tracking-widest uppercase font-semibold text-gold mb-4">
            Our Infrastructure
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-forest mb-6">
            Our Business Units
          </h2>
          <p className="text-lg text-forest/70 max-w-2xl mx-auto">
            Three rice mills in Raichur District, Karnataka & a trading firm in Bangalore
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {mills.map((mill, index) => (
            <div
              key={mill.name}
              className="group p-6 rounded-2xl bg-bone shadow-card hover:shadow-card-hover transition-shadow duration-300 border border-border/30 text-center"
              data-testid={`mill-card-${index}`}
            >
              <div className="w-24 h-24 mx-auto mb-6 rounded-xl overflow-hidden bg-white flex items-center justify-center p-2">
                <img 
                  src={mill.logo} 
                  alt={mill.name} 
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="inline-block bg-gold/20 text-gold-dark text-xs font-semibold px-3 py-1 rounded-full mb-3">
                {mill.type}
              </span>
              <h3 className="font-heading text-lg font-bold text-forest mb-3">
                {mill.name}
              </h3>
              <p className="text-forest/70 text-sm leading-relaxed">
                {mill.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Products Section
const ProductsSection = () => {
  const products = [
    {
      name: "Sona Masoori Raw Rice (Old)",
      description: "Premium aged raw rice with excellent aroma and taste, ideal for everyday cooking",
      image: "https://customer-assets.emergentagent.com/job_grain-grinder-1/artifacts/uq3xwjle_Sona%20Masoori%20Raw%20Rice%20Old.png",
      category: "Raw Rice",
    },
    {
      name: "Sona Masoori Steam Rice",
      description: "Steam processed for better nutrition retention and fluffy texture",
      image: "https://customer-assets.emergentagent.com/job_grain-grinder-1/artifacts/231r75io_Sona%20Masoori%20Steam%20Rice.png",
      category: "Steam Rice",
    },
    {
      name: "Kolam Raw Rice (Old)",
      description: "Medium grain with excellent taste, popular in South Indian cuisine",
      image: "https://customer-assets.emergentagent.com/job_grain-grinder-1/artifacts/7al7iik7_Kolam%20Raw%20Rice%20Old.png",
      category: "Raw Rice",
    },
    {
      name: "RNR Raw Rice (Old)",
      description: "Premium RNR variety known for its distinct flavor and aroma",
      image: "https://customer-assets.emergentagent.com/job_grain-grinder-1/artifacts/3dfcm0as_RNR%20Raw%20Rice%20Old.png",
      category: "Raw Rice",
    },
    {
      name: "Sona Masoori Boiled Rice",
      description: "Parboiled for extra nutrition, easy digestion and great taste",
      image: "https://customer-assets.emergentagent.com/job_grain-grinder-1/artifacts/p5sxqnd6_Sona%20Boiled%20Rice.png",
      category: "Boiled Rice",
    },
  ];

  return (
    <section
      id="products"
      className="py-20 md:py-32 bg-bone relative"
      data-testid="products-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-sm tracking-widest uppercase font-semibold text-gold mb-4">
            Our Products
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-forest mb-6">
            Premium Rice Varieties
          </h2>
          <p className="text-lg text-forest/70 max-w-2xl mx-auto">
            From Sona Masoori to RNR, we offer a complete range of raw, steam, 
            and boiled rice along with rice byproducts.
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
          {products.map((product, index) => (
            <div
              key={product.name}
              className="group bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover border border-border/30 transition-all duration-300 hover:-translate-y-2"
              data-testid={`product-card-${index}`}
            >
              {/* Product Image */}
              <div className="relative h-48 mb-5 flex items-center justify-center overflow-hidden rounded-xl bg-gradient-to-b from-sage/30 to-bone">
                <img
                  src={product.image}
                  alt={product.name}
                  className="h-44 w-auto object-contain transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              
              {/* Category Badge */}
              <span className="inline-block bg-gold/15 text-gold-dark text-xs font-semibold px-3 py-1 rounded-full mb-3">
                {product.category}
              </span>
              
              {/* Product Name */}
              <h3 className="font-heading text-lg font-bold text-forest mb-2 group-hover:text-gold transition-colors duration-300">
                {product.name}
              </h3>
              
              {/* Description */}
              <p className="text-sm text-forest/60 leading-relaxed mb-4">
                {product.description}
              </p>
              
              {/* CTA Button */}
              <Button
                size="sm"
                className="w-full rounded-full bg-forest hover:bg-gold text-white transition-colors duration-300"
                data-testid={`product-enquire-${index}`}
              >
                Enquire Now
              </Button>
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <p className="text-forest/70 mb-6 text-lg">
            We also offer <span className="font-semibold text-forest">RNR Steam Rice, RNR Boiled Rice, Sona Masoori Semi Old</span> and rice byproducts
          </p>
          <Button
            className="bg-gold hover:bg-gold-dark text-white rounded-full px-10 py-6 text-lg font-semibold transition-all duration-300 hover:shadow-lg"
            data-testid="view-all-products"
          >
            Contact for Full Catalog
          </Button>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Factory,
      title: "Rice Processing",
      description: "Complete processing services for raw, steam, and parboiled rice with three specialized mills.",
    },
    {
      icon: Package,
      title: "Bulk & Retail Packaging",
      description: "Flexible packaging options from 1kg to 50kg bags for retailers, supermarkets, and e-commerce.",
    },
    {
      icon: Truck,
      title: "Pan-India Delivery",
      description: "Reliable delivery across Karnataka and India. Bangalore hub for quick city deliveries.",
    },
    {
      icon: ShieldCheck,
      title: "Quality Assurance",
      description: "Rigorous quality testing for moisture, purity, and grading before every dispatch.",
    },
    {
      icon: Users,
      title: "E-commerce & Supermarket Supply",
      description: "Dedicated B2B supply chain for online platforms and retail supermarkets since 2019.",
    },
    {
      icon: Wheat,
      title: "Rice Byproducts",
      description: "Complete range of rice byproducts including bran, broken rice, and husk for various industries.",
    },
  ];

  return (
    <section
      id="services"
      className="py-20 md:py-32 bg-white relative"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-sm tracking-widest uppercase font-semibold text-gold mb-4">
            What We Offer
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-forest mb-6">
            Our Services
          </h2>
          <p className="text-lg text-forest/70 max-w-2xl mx-auto">
            End-to-end rice processing and distribution services from 
            Raichur to Bangalore and beyond.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={service.title}
              className="group p-8 rounded-2xl bg-bone shadow-card hover:shadow-card-hover transition-shadow duration-300"
              data-testid={`service-card-${index}`}
            >
              <div className="service-icon w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <service.icon className="h-8 w-8 text-forest" />
              </div>
              <h3 className="font-heading text-xl font-bold text-forest mb-3">
                {service.title}
              </h3>
              <p className="text-forest/70 leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Quality Badges Section
const QualitySection = () => {
  const badges = [
    { icon: ShieldCheck, label: "FSSAI Certified", sublabel: "Food Safety Standards" },
    { icon: Award, label: "ISO 22000:2018", sublabel: "Quality Management" },
    { icon: Leaf, label: "Organic Certified", sublabel: "Chemical Free" },
    { icon: Star, label: "5-Star Rating", sublabel: "Customer Trust" },
  ];

  return (
    <section className="py-16 bg-forest" data-testid="quality-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {badges.map((badge, index) => (
            <div
              key={badge.label}
              className="quality-badge text-center p-6 rounded-2xl"
              data-testid={`quality-badge-${index}`}
            >
              <badge.icon className="h-10 w-10 text-gold mx-auto mb-3" />
              <p className="font-semibold text-forest">{badge.label}</p>
              <p className="text-sm text-forest/60">{badge.sublabel}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Rajesh Kumar",
      role: "Restaurant Owner",
      content: "Golden Harvest has been our trusted supplier for over 10 years. The quality of their Basmati rice is unmatched. Our customers always compliment our biryani!",
      rating: 5,
    },
    {
      name: "Priya Sharma",
      role: "Homemaker",
      content: "I've tried many brands, but nothing compares to the taste and aroma of Golden Harvest rice. It's become a staple in our household.",
      rating: 5,
    },
    {
      name: "Mohammed Ali",
      role: "Wholesaler",
      content: "Their bulk packaging and timely delivery has helped my business grow. The consistent quality keeps my customers coming back.",
      rating: 5,
    },
    {
      name: "Lakshmi Devi",
      role: "Caterer",
      content: "For large events, I only trust Golden Harvest. Their rice cooks perfectly every time and the flavor is always excellent.",
      rating: 5,
    },
  ];

  return (
    <section
      id="testimonials"
      className="py-20 md:py-32 bg-white relative"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-sm tracking-widest uppercase font-semibold text-gold mb-4">
            Testimonials
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-forest mb-6">
            What Our Customers Say
          </h2>
        </div>

        <Carousel
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full"
          data-testid="testimonials-carousel"
        >
          <CarouselContent className="-ml-4">
            {testimonials.map((testimonial, index) => (
              <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <Card className="testimonial-card border-0 shadow-card h-full">
                  <CardContent className="p-8">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-gold text-gold" />
                      ))}
                    </div>
                    <p className="text-forest/80 leading-relaxed mb-6 italic">
                      "{testimonial.content}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center">
                        <span className="font-heading font-bold text-forest">
                          {testimonial.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-forest">{testimonial.name}</p>
                        <p className="text-sm text-forest/60">{testimonial.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex -left-4 bg-white shadow-card border-0 hover:bg-bone" />
          <CarouselNext className="hidden md:flex -right-4 bg-white shadow-card border-0 hover:bg-bone" />
        </Carousel>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success("Message sent successfully! We'll get back to you soon.");
    setFormData({ name: "", email: "", phone: "", message: "" });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-32 bg-bone relative"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <div>
            <p className="text-sm tracking-widest uppercase font-semibold text-gold mb-4">
              Get In Touch
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-forest mb-6">
              Contact Us
            </h2>
            <p className="text-lg text-forest/70 leading-relaxed mb-8">
              Whether you're a retailer, e-commerce platform, or supermarket looking 
              for quality rice supply, we'd love to hear from you.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-forest" />
                </div>
                <div>
                  <p className="font-semibold text-forest">Mills Location</p>
                  <p className="text-forest/70">Raichur District<br />Karnataka State, India</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center flex-shrink-0">
                  <Factory className="h-5 w-5 text-forest" />
                </div>
                <div>
                  <p className="font-semibold text-forest">Trading Office</p>
                  <p className="text-forest/70">Bangalore, Karnataka<br />(E-commerce & Supermarket Hub)</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center flex-shrink-0">
                  <Phone className="h-5 w-5 text-forest" />
                </div>
                <div>
                  <p className="font-semibold text-forest">Call Us</p>
                  <p className="text-forest/70">+91 95386 36345</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center flex-shrink-0">
                  <Mail className="h-5 w-5 text-forest" />
                </div>
                <div>
                  <p className="font-semibold text-forest">Email Us</p>
                  <p className="text-forest/70">Nandakishore@ambatigroups.com</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 shadow-card">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium text-forest mb-2">
                  Full Name
                </label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  className="rounded-lg border-border/50 focus:border-gold focus:ring-gold"
                  required
                  data-testid="contact-name-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-forest mb-2">
                    Email
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="rounded-lg border-border/50 focus:border-gold focus:ring-gold"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-forest mb-2">
                    Phone
                  </label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 XXXXX XXXXX"
                    className="rounded-lg border-border/50 focus:border-gold focus:ring-gold"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-forest mb-2">
                  Message
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your requirements..."
                  rows={4}
                  className="rounded-lg border-border/50 focus:border-gold focus:ring-gold resize-none"
                  required
                  data-testid="contact-message-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-gold hover:bg-gold-dark text-white rounded-full py-6 text-lg font-semibold transition-colors duration-200"
                data-testid="contact-submit-button"
              >
                Send Message
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer-gradient py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <a href="#home" className="flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_grain-grinder-1/artifacts/7d57xsgc_AMBATI%20LOGO-2.png" 
                alt="Ambati Ventures Logo" 
                className="h-12 w-12 object-contain"
              />
              <span className="font-heading text-2xl font-bold text-forest">
                AMBATI VENTURES
              </span>
            </a>
            <p className="text-forest/70 leading-relaxed mb-6">
              Premium quality rice processing since 1982. Three specialized mills 
              in Karnataka serving India.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-bold text-forest mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {["Home", "About", "Our Mills", "Products", "Contact"].map((link) => (
                <li key={link}>
                  <a
                    href={`#${link.toLowerCase().replace(" ", "-")}`}
                    className="text-forest/70 hover:text-gold transition-colors duration-200"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-heading font-bold text-forest mb-4">Our Products</h4>
            <ul className="space-y-3">
              {["Sona Masoori Rice", "Kolam Rice", "RNR Rice", "Steam Rice", "Boiled Rice"].map((product) => (
                <li key={product}>
                  <span className="text-forest/70">{product}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading font-bold text-forest mb-4">Newsletter</h4>
            <p className="text-forest/70 mb-4">
              Subscribe for updates on new products and special offers.
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="Your email"
                className="rounded-full border-border/50"
                data-testid="newsletter-input"
              />
              <Button
                className="bg-gold hover:bg-gold-dark text-white rounded-full px-6 transition-colors duration-200"
                data-testid="newsletter-subscribe-button"
              >
                Subscribe
              </Button>
            </div>
          </div>
        </div>

        <div className="border-t border-border/50 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-forest/60 text-sm">
            © {currentYear} AMBATI VENTURES. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-forest/60 hover:text-gold text-sm transition-colors duration-200">
              Privacy Policy
            </a>
            <a href="#" className="text-forest/60 hover:text-gold text-sm transition-colors duration-200">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/919538636345?text=Hi,%20I'm%20interested%20in%20AMBATI%20VENTURES%20rice%20products"
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg"
      data-testid="whatsapp-button"
    >
      <MessageCircle className="h-7 w-7 text-white" />
    </a>
  );
};

// Main App Component
function App() {
  return (
    <div className="min-h-screen">
      <Navigation />
      <HeroSection />
      <AboutSection />
      <MillsSection />
      <ProductsSection />
      <ServicesSection />
      <QualitySection />
      <TestimonialsSection />
      <ContactSection />
      <Footer />
      <WhatsAppButton />
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
